import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import _ from "lodash";
import clsx from "clsx";
import { Form, useField } from "react-final-form";
import { FORM_ERROR, FormApi } from "final-form";
import { InformationCircleIcon, PlusIcon } from "@heroicons/react/24/outline";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid";
import { Button as AquiloButton, Modal, Toggle, ToolTip, useSnackBar } from "@truevault/aquilo";
import { requiredString } from "../../../util/validation";
import { sleep } from "../../../util/sleep";
import { lexicalJoin } from "../../../util/text";
import { usePrimaryOrganizationId } from "../../../hooks";
import {
  useCMPCustomization,
  useCMPStagingCustomization,
  usePrivacyCenter,
} from "../../../hooks/usePrivacyCenter";
import { useCookieScanResult } from "../../../hooks/useCookieScanResult";
import { Routes } from "../../../root/routes";
import { NetworkRequestState } from "../../../../common/models";
import {
  CMPSettingsDto,
  BannerCopyVariant,
  DismissalButtonCopyVariant,
  ConsentBannerUSVisibility,
  ConsentManagementVariant,
  ConsentRequiredMode,
  ConsentChoiceRequiredBannerType,
  CustomConsentManagementVariant,
  InformationalBannerDismissalStyle,
} from "../../../../common/service/server/dto/CMPSettingsDto";
import { PrivacyCenterDto } from "../../../../common/service/server/dto/PrivacyCenterDto";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";
import { ProcessingRegion } from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { Iso3166Region } from "../../../../common/service/server/Types";
import { EEA_UK_CH_COUNTRY_NAMES, OVERRIDEABLE_COUNTRIES } from "./ConsentBannerCountries";
import { ConsentManagementVariantRow } from "../../organisms/settings/rows/consentBanner/ConsentManagementVariantRow";
import { ConsentManagementVariantCustomRow } from "../../organisms/settings/rows/consentBanner/ConsentManagementVariantCustomRow";
import { ConsentBannerBackgroundColorRow } from "../../organisms/settings/rows/consentBanner/ConsentBannerBackgroundColorRow";
import {
  ConsentBannerIconColorRow,
  ICON_PRIMARY_COLOR_DEFAULT,
  ICON_SECONDARY_COLOR_DEFAULT,
} from "../../organisms/settings/rows/consentBanner/ConsentBannerIconColorRow";
import {
  ConsentBannerScreenPositionRow,
  SCREEN_POSITION_LABELS,
} from "../../organisms/settings/rows/consentBanner/ConsentBannerScreenPositionRow";
import { ConsentBannerUSTitleRow } from "../../organisms/settings/rows/consentBanner/ConsentBannerUSTitleRow";
import { ConsentBannerUSTextRow } from "../../organisms/settings/rows/consentBanner/ConsentBannerUSTextRow";
import { ConsentBannerUSButtonRow } from "../../organisms/settings/rows/consentBanner/ConsentBannerUSButtonRow";
import { ConsentBannerUSDismissalRow } from "../../organisms/settings/rows/consentBanner/ConsentBannerUSDismissalRow";
import { ConsentBannerCopyOverrideRow } from "../../organisms/settings/rows/consentBanner/ConsentBannerCopyOverrideRow";
import { ConsentBannerOptInButtonTextRow } from "../../organisms/settings/rows/consentBanner/ConsentBannerOptInButtonTextRow";
import { ConsentBannerDismissButtonTextRow } from "../../organisms/settings/rows/consentBanner/ConsentBannerDismissButtonTextRow";
import { ConsentBannerPreferencesDisclaimerRow } from "../../organisms/settings/rows/consentBanner/ConsentBannerPreferencesDisclaimerRow";
import { ConsentBannerConsentRequiredRow } from "../../organisms/settings/rows/consentBanner/ConsentBannerConsentRequiredRow";
import { ConsentBannerAllowCSSOverridesRow } from "../../organisms/settings/rows/consentBanner/ConsentBannerAllowCSSOverridesRow";
import { ConsentBannerPreviewURLRow } from "../../organisms/settings/rows/consentBanner/ConsentBannerPreviewURLRow";
import { ConsentEnvironmentHeader } from "../../organisms/consentBanner/ConsentEnvironmentHeader";
import { ConsentGroupHeader, ConsentSection } from "../../organisms/consentBanner/ConsentSection";
import { ConsentSettingRow } from "../../organisms/consentBanner/ConsentSettingRow";
import { ConsentNote } from "../../organisms/consentBanner/ConsentNote";
import {
  ConsentOnThisPage,
  ConsentOnThisPageItem,
} from "../../organisms/consentBanner/ConsentOnThisPage";
import {
  ConsentEnvironment,
  websiteDomainFromPrivacyCenterUrl,
} from "../../organisms/consentBanner/consentEnvironment";
import { useTopBannerHeight } from "../../../../common/hooks/useTopBannerHeight";
import { useElementHeight } from "../../../../common/hooks/useElementHeight";
import { differingSettings } from "../../organisms/consentBanner/consentSettingsDiff";
import {
  ConsentBannerFormValues,
  regionsWithoutABanner,
  ShownBannerRegions,
  withClearedSettings,
} from "../../organisms/consentBanner/consentPublishSettings";

interface ConsentBannerSettingsProps extends CMPSettingsDto {
  onUpdateCMPCustomization: (newCMPCustomization: CMPSettingsDto) => Promise<void>;
  organization: OrganizationDto;
  privacyCenterURL:
    | string
    | undefined /* The URL of the privacy center - we can usually guess the org's website (and a default preview URL) from it. */;
  isConfirmedCustomizableCMPVersion: boolean;
  environment: ConsentEnvironment;
  onEnvironmentChange: (environment: ConsentEnvironment) => void;
  // False until this environment has been published once. Staging's form then starts from the production settings.
  hasSavedSettings: boolean;
  // The other environment's saved settings, for copying them in. Null when staging has none yet.
  otherEnvironmentSettings: CMPSettingsDto | null;
  // Help card and setup notes, shown between the environment header and the settings.
  intro?: React.ReactNode;
  // This environment's saved settings couldn't be loaded. Publishing is blocked: staging publishes replace the whole
  // saved record, so publishing values that only look unsaved would wipe it.
  loadFailed?: boolean;
  // Put focus on the environment switcher when the form mounts, e.g. after switching environments remounted it.
  focusEnvironmentSwitcher?: boolean;
  // Told when the form gains or loses unpublished changes, so the page can ask before leaving the tab.
  onDirtyChange?: (dirty: boolean) => void;
}

export interface ConsentBannerSettingsHandle {
  /** Submits the form. Returns true if submission was successful, false if there were validation errors. */
  submitForm: () => Promise<boolean>;
  /** Whether the form's current values, unpublished edits included, differ from another environment's settings. */
  differsFrom: (settings: CMPSettingsDto) => boolean;
  /** The settings a publish of the form's current values would send, or null before the form mounts. */
  getPublishedSettings: () => CMPSettingsDto | null;
}

export interface OverrideableRegion {
  region: Iso3166Region;
  label: string;
  abbreviation: string;
  isUSP: boolean;
  isNonUSP: boolean;
  isChildrenConsent?: boolean;
  dataCollectionAge?: number;
}

// reference the constant USP_STATES=['VA', 'CO', 'CT', 'UT', 'TX', 'OR', 'MT', 'FL', 'IA', 'DE', 'NH', 'NE', 'NJ', 'MD', 'NV', ...] from
// the CMP and keep this in sync so we can show each state in the appropriate "See States" hover list
export const OVERRIDEABLE_REGIONS: OverrideableRegion[] = [
  { region: "US-AL", label: "Alabama", abbreviation: "AL", isUSP: false, isNonUSP: true },
  { region: "US-AK", label: "Alaska", abbreviation: "AK", isUSP: false, isNonUSP: true },
  { region: "US-AZ", label: "Arizona", abbreviation: "AZ", isUSP: false, isNonUSP: true },
  { region: "US-AR", label: "Arkansas", abbreviation: "AR", isUSP: false, isNonUSP: true },
  {
    region: "US-CA",
    label: "California",
    abbreviation: "CA",
    isUSP: false,
    isNonUSP: false,
    isChildrenConsent: true,
    dataCollectionAge: 16,
  },
  {
    region: "US-CO",
    label: "Colorado",
    abbreviation: "CO",
    isUSP: true,
    isNonUSP: false,
    isChildrenConsent: true,
    dataCollectionAge: 18,
  },
  {
    region: "US-CT",
    label: "Connecticut",
    abbreviation: "CT",
    isUSP: true,
    isNonUSP: false,
    isChildrenConsent: true,
    dataCollectionAge: 18,
  },
  {
    region: "US-DE",
    label: "Delaware",
    abbreviation: "DE",
    isUSP: true,
    isNonUSP: false,
    isChildrenConsent: true,
    dataCollectionAge: 18,
  },
  {
    region: "US-DC",
    label: "District of Columbia",
    abbreviation: "DC",
    isUSP: false,
    isNonUSP: true,
  },
  { region: "US-FL", label: "Florida", abbreviation: "FL", isUSP: true, isNonUSP: false },
  { region: "US-GA", label: "Georgia", abbreviation: "GA", isUSP: false, isNonUSP: true },
  { region: "US-HI", label: "Hawaii", abbreviation: "HI", isUSP: false, isNonUSP: true },
  { region: "US-ID", label: "Idaho", abbreviation: "ID", isUSP: false, isNonUSP: true },
  { region: "US-IL", label: "Illinois", abbreviation: "IL", isUSP: false, isNonUSP: true },
  { region: "US-IN", label: "Indiana", abbreviation: "IN", isUSP: true, isNonUSP: false },
  { region: "US-IA", label: "Iowa", abbreviation: "IA", isUSP: true, isNonUSP: false },
  { region: "US-KS", label: "Kansas", abbreviation: "KS", isUSP: false, isNonUSP: true },
  { region: "US-KY", label: "Kentucky", abbreviation: "KY", isUSP: true, isNonUSP: false },
  { region: "US-LA", label: "Louisiana", abbreviation: "LA", isUSP: false, isNonUSP: true },
  { region: "US-ME", label: "Maine", abbreviation: "ME", isUSP: false, isNonUSP: true },
  {
    region: "US-MD",
    label: "Maryland",
    abbreviation: "MD",
    isUSP: true,
    isNonUSP: false,
    isChildrenConsent: true,
    dataCollectionAge: 18,
  },
  { region: "US-MA", label: "Massachusetts", abbreviation: "MA", isUSP: false, isNonUSP: true },
  { region: "US-MI", label: "Michigan", abbreviation: "MI", isUSP: false, isNonUSP: true },
  { region: "US-MN", label: "Minnesota", abbreviation: "MN", isUSP: true, isNonUSP: false },
  { region: "US-MS", label: "Mississippi", abbreviation: "MS", isUSP: false, isNonUSP: true },
  { region: "US-MO", label: "Missouri", abbreviation: "MO", isUSP: false, isNonUSP: true },
  {
    region: "US-MT",
    label: "Montana",
    abbreviation: "MT",
    isUSP: true,
    isNonUSP: false,
    isChildrenConsent: true,
    dataCollectionAge: 18,
  },
  { region: "US-NE", label: "Nebraska", abbreviation: "NE", isUSP: true, isNonUSP: false },
  { region: "US-NV", label: "Nevada", abbreviation: "NV", isUSP: true, isNonUSP: false },
  {
    region: "US-NH",
    label: "New Hampshire",
    abbreviation: "NH",
    isUSP: true,
    isNonUSP: false,
    isChildrenConsent: true,
    dataCollectionAge: 16,
  },
  {
    region: "US-NJ",
    label: "New Jersey",
    abbreviation: "NJ",
    isUSP: true,
    isNonUSP: false,
    isChildrenConsent: true,
    dataCollectionAge: 17,
  },
  { region: "US-NM", label: "New Mexico", abbreviation: "NM", isUSP: false, isNonUSP: true },
  { region: "US-NY", label: "New York", abbreviation: "NY", isUSP: false, isNonUSP: true },
  { region: "US-NC", label: "North Carolina", abbreviation: "NC", isUSP: false, isNonUSP: true },
  { region: "US-ND", label: "North Dakota", abbreviation: "ND", isUSP: false, isNonUSP: true },
  { region: "US-OH", label: "Ohio", abbreviation: "OH", isUSP: false, isNonUSP: true },
  { region: "US-OK", label: "Oklahoma", abbreviation: "OK", isUSP: false, isNonUSP: true },
  { region: "US-OR", label: "Oregon", abbreviation: "OR", isUSP: true, isNonUSP: false },
  { region: "US-PA", label: "Pennsylvania", abbreviation: "PA", isUSP: false, isNonUSP: true },
  { region: "US-RI", label: "Rhode Island", abbreviation: "RI", isUSP: true, isNonUSP: false },
  { region: "US-SC", label: "South Carolina", abbreviation: "SC", isUSP: false, isNonUSP: true },
  { region: "US-SD", label: "South Dakota", abbreviation: "SD", isUSP: false, isNonUSP: true },
  { region: "US-TN", label: "Tennessee", abbreviation: "TN", isUSP: true, isNonUSP: false },
  { region: "US-TX", label: "Texas", abbreviation: "TX", isUSP: true, isNonUSP: false },
  { region: "US-UT", label: "Utah", abbreviation: "UT", isUSP: true, isNonUSP: false },
  { region: "US-VT", label: "Vermont", abbreviation: "VT", isUSP: false, isNonUSP: true },
  { region: "US-VA", label: "Virginia", abbreviation: "VA", isUSP: true, isNonUSP: false },
  { region: "US-WA", label: "Washington", abbreviation: "WA", isUSP: false, isNonUSP: true },
  { region: "US-WV", label: "West Virginia", abbreviation: "WV", isUSP: false, isNonUSP: true },
  { region: "US-WI", label: "Wisconsin", abbreviation: "WI", isUSP: false, isNonUSP: true },
  { region: "US-WY", label: "Wyoming", abbreviation: "WY", isUSP: false, isNonUSP: true },
];

const NoCookiesCallout = () => (
  <ConsentNote tone="tip">
    Before configuring your banner, we need to know what cookies you have on your site. Visit your{" "}
    <a href={Routes.cookies.root}>Cookie Scanner</a> to get started, then return here to configure
    your banner.
  </ConsentNote>
);

const LOADING_PREVIEW_DELAY_SECONDS = 11; // Delay to allow preview time to publish. Err on side of longer since it would be embarrassing to launch and have it not be there.
export const INFORMATIONAL_BANNER_TITLE_DEFAULT_TEXT = "We Take Privacy Seriously";

export const INFORMATIONAL_BANNER_DEFAULT_TEXT =
  "This site uses cookies and third-party technologies that may be used to track users' interactions with our website, provide communication tools, personalize ads, and more. By continuing to use this site, you are agreeing to the use of these technologies and the processing of your personal information by third parties as described in our %{privacy_request.privacy_policy} and terms of service.";
const INFORMATIONAL_BANNER_DEFAULT_TEXT_LEGACY =
  "This site uses cookies and third-party technologies that may be used to track users' interactions with our website, provide communication tools, personalize ads, and more. By continuing to use this site, you are agreeing to the use of these technologies and the processing of your personal information by third parties as described in our privacy policy and terms of service.";
const FLORIDA_REGION: Iso3166Region = "US-FL";
const CALIFORNIA_CONSENT_MANAGEMENT_VARIANT_FIELD = "usConsentManagementVariantCA";
const FLORIDA_CONSENT_MANAGEMENT_VARIANT_FIELD = "usConsentManagementVariantFL";
const PRIVACY_STATES_CONSENT_MANAGEMENT_VARIANT_FIELD = "usConsentManagementVariantUSP";
const NON_PRIVACY_STATES_CONSENT_MANAGEMENT_VARIANT_FIELD = "usConsentManagementVariantNonUSP";
const CHILDREN_CONSENT_MANAGEMENT_VARIANT_FIELD = "usConsentManagementVariantMinors";
const WIRETAPPING_LITIGATION_MAP_URL =
  "https://www.fisherphillips.com/en/resources-and-innovation/trackers-and-maps/wiretapping-litigation-map";
const CIPA_LEARN_MORE_URL = "https://www.truevault.com/learn/california-wiretapping-lawsuits";

const WiretappingNoticeLink = ({ href, children }: { href: string; children: React.ReactNode }) => (
  <a href={href} target="_blank" rel="noopener noreferrer">
    {children}
    <ArrowTopRightOnSquareIcon className="ml-[2px] inline h-[14px] w-[14px] align-[-2px]" />
    <span className="sr-only"> (opens in a new tab)</span>
  </a>
);

// These suggested defaults are from the polaris-i18n project.
export const OPT_OUT_BANNER_DEFAULT_TEXT =
  "We collect data through web cookies and similar technologies. See our %{privacy_request.privacy_policy} or manage choices below.";
export const OPT_IN_BANNER_DEFAULT_TEXT =
  "By clicking Accept, you agree to the use of cookies and tracking technology for personalization, analytics, and advertising. See our %{privacy_request.privacy_policy} for more info.";
const isKnownInformationalBannerDefaultText = (text: string): boolean => {
  const normalizedText = text.trim();
  return [INFORMATIONAL_BANNER_DEFAULT_TEXT, INFORMATIONAL_BANNER_DEFAULT_TEXT_LEGACY].includes(
    normalizedText,
  );
};

export const INFORMATIONAL_BANNER_ACCEPT_BUTTON_DEFAULT_TEXT = "Okay";

const FORM_FIELDS_TO_HUMAN_DESCRIPTION = {
  usConsentManagementVariant: "US Banner",
  [CALIFORNIA_CONSENT_MANAGEMENT_VARIANT_FIELD]: "California",
  [FLORIDA_CONSENT_MANAGEMENT_VARIANT_FIELD]: "Florida",
  [PRIVACY_STATES_CONSENT_MANAGEMENT_VARIANT_FIELD]: "Privacy states",
  [NON_PRIVACY_STATES_CONSENT_MANAGEMENT_VARIANT_FIELD]: "Non-privacy states",
  [CHILDREN_CONSENT_MANAGEMENT_VARIANT_FIELD]: "Children consent states",
  canadaConsentManagementVariant: "Canada Banner",
  eeaukConsentManagementVariant: "EEA/UK/CH Banner",
  customConsentManagementVariants: "Custom Banner Regions",
  bannerIconColorPrimary: "Icon Primary Color",
  bannerIconColorSecondary: "Icon Secondary Color",
  bannerBackgroundColor: "Banner Background Color",
  bannerScreenPosition: "Banner Screen Position",
  informationalConsentManagementVariant: "US Banner Enabled",
  informationalBannerTitle: "US Banner Title",
  informationalBannerText: "US Banner Text",
  informationalBannerAcceptButtonText: "US Banner Accept Button Text",
  informationalBannerDismissalStyle: "Informational Banner Dismissal",
  bannerTextOptInEn: "Custom Opt-In Banner Text",
  bannerTextOptOutEn: "Custom Opt-Out Banner Text",
  bannerCopyVariant: "Opt-In Banner Button Text",
  bannerDismissalButtonCopyVariant: "Preference Screen Dismiss Button",
  bannerPreferencesDisclaimerTextEn: "Preferences Screen Disclaimer Text",
  gdprConsentManagementEnabled: "Consent Management Enabled",
  previewURL: "Preview URL",
  consentChoiceRequiredBannerTypes: "Require Consent Choice",
  allowCSSOverrides: "Allow Custom Styles",
};

// Names for the settings listed when they differ between production and staging, matching the form's row titles.
const ENVIRONMENT_DIFF_SETTING_LABELS: Record<string, string> = {
  [CALIFORNIA_CONSENT_MANAGEMENT_VARIANT_FIELD]: "California banner",
  [FLORIDA_CONSENT_MANAGEMENT_VARIANT_FIELD]: "Florida banner",
  [CHILDREN_CONSENT_MANAGEMENT_VARIANT_FIELD]: "Children consent states banner",
  [PRIVACY_STATES_CONSENT_MANAGEMENT_VARIANT_FIELD]: "Privacy states banner",
  [NON_PRIVACY_STATES_CONSENT_MANAGEMENT_VARIANT_FIELD]: "Non-privacy states banner",
  customConsentManagementVariants: "Advanced banner visibility states",
  canadaConsentManagementVariant: "Canada banner",
  eeaukConsentManagementVariant: "EEA, UK, and Switzerland banner",
  globalConsentManagementVariantDefault: "Global consent banner",
  globalConsentManagementVariants: "Global consent country overrides",
  bannerBackgroundColor: "Background",
  bannerIconColorPrimary: "Icon primary color",
  bannerIconColorSecondary: "Icon secondary color",
  bannerScreenPosition: "Screen position",
  informationalBannerTitle: "Banner title",
  bannerTextOptInEn: "Opt-in banner text",
  bannerCopyVariant: "Opt-in button labels",
  bannerTextOptOutEn: "Opt-out banner text",
  informationalBannerAcceptButtonText: "Accept button",
  informationalBannerText: "Informational banner text",
  informationalBannerDismissalStyle: "Informational banner dismiss options",
  bannerDismissalButtonCopyVariant: "Preferences screen close button",
  bannerPreferencesDisclaimerTextEn: "Preferences screen disclaimer",
  consentChoiceRequiredBannerTypes: "Require a consent choice",
  allowCSSOverrides: "Custom styles",
  usBannerVisibility: "US banner visibility",
};

const ALL_CONSENT_CHOICE_REQUIRED_BANNER_TYPES = [
  ConsentChoiceRequiredBannerType.GDPR,
  ConsentChoiceRequiredBannerType.OPT_IN,
  ConsentChoiceRequiredBannerType.OPT_OUT,
  ConsentChoiceRequiredBannerType.INFORMATIONAL,
];

const getInitialConsentChoiceRequiredBannerTypes = (
  consentChoiceRequiredBannerTypes: ConsentChoiceRequiredBannerType[] | undefined,
  consentRequiredMode: ConsentRequiredMode | undefined,
): ConsentChoiceRequiredBannerType[] => {
  if (consentChoiceRequiredBannerTypes && consentChoiceRequiredBannerTypes.length > 0) {
    return consentChoiceRequiredBannerTypes;
  }
  return consentRequiredMode === ConsentRequiredMode.EVERYWHERE
    ? ALL_CONSENT_CHOICE_REQUIRED_BANNER_TYPES
    : [];
};

const setLegacyConsentRequiredMode = (
  formVals: Partial<CMPSettingsDto>,
): Partial<CMPSettingsDto> => {
  const selectedBannerTypes = formVals.consentChoiceRequiredBannerTypes ?? [];
  const allBannerTypesSelected = ALL_CONSENT_CHOICE_REQUIRED_BANNER_TYPES.every((bannerType) =>
    selectedBannerTypes.includes(bannerType),
  );

  formVals.consentRequiredMode = allBannerTypesSelected
    ? ConsentRequiredMode.EVERYWHERE
    : ConsentRequiredMode.NONE;
  return formVals;
};

const getFloridaConsentManagementVariant = (
  customVariants: CustomConsentManagementVariant[] | undefined,
  fallbackVariant: ConsentManagementVariant | undefined,
): ConsentManagementVariant | undefined =>
  customVariants?.find((variant) => variant.region === FLORIDA_REGION)?.variant ?? fallbackVariant;

const removeFloridaCustomConsentManagementVariant = (
  customVariants: CustomConsentManagementVariant[] | undefined,
): CustomConsentManagementVariant[] =>
  (customVariants ?? []).filter((variant) => variant.region !== FLORIDA_REGION);

const applyFloridaCustomConsentManagementVariant = (
  customVariants: CustomConsentManagementVariant[] | undefined,
  floridaVariant: ConsentManagementVariant | undefined,
): CustomConsentManagementVariant[] => {
  const nextCustomVariants = removeFloridaCustomConsentManagementVariant(customVariants);

  if (floridaVariant) {
    nextCustomVariants.push({
      region: FLORIDA_REGION,
      variant: floridaVariant,
    });
  }

  return nextCustomVariants;
};

const normalizeFloridaConsentManagementVariant = (
  formVals: ConsentBannerFormValues,
): CMPSettingsDto => {
  const { [FLORIDA_CONSENT_MANAGEMENT_VARIANT_FIELD]: floridaVariant, ...cmpSettings } = formVals;

  return {
    ...cmpSettings,
    customConsentManagementVariants: applyFloridaCustomConsentManagementVariant(
      formVals.customConsentManagementVariants,
      floridaVariant,
    ),
  } as CMPSettingsDto;
};

const getUSBannerConsentManagementVariantFields = (
  showCalifornia: boolean,
  showMinorsConsentSettings: boolean,
): string[] => [
  ...(showCalifornia ? [CALIFORNIA_CONSENT_MANAGEMENT_VARIANT_FIELD] : []),
  FLORIDA_CONSENT_MANAGEMENT_VARIANT_FIELD,
  // Keep Children Consent States beneath Florida whether it appears alongside California
  // or replaces California for under-16 collection settings.
  ...(showMinorsConsentSettings ? [CHILDREN_CONSENT_MANAGEMENT_VARIANT_FIELD] : []),
  PRIVACY_STATES_CONSENT_MANAGEMENT_VARIANT_FIELD,
  NON_PRIVACY_STATES_CONSENT_MANAGEMENT_VARIANT_FIELD,
];

const getAdvancedUSOverrideRegions = (): OverrideableRegion[] =>
  OVERRIDEABLE_REGIONS.filter((region) => region.region !== FLORIDA_REGION);

/** Registers a field that has no visible input, so react-final-form counts it toward `dirty` and submits it. */
const RegisterField = ({ name }: { name: string }) => {
  useField(name, { subscription: {} });
  return null;
};

/** Reports the form's unpublished-changes state to the page; reports none when the form goes away. */
const ReportDirty = ({
  dirty,
  onDirtyChange,
}: {
  dirty: boolean;
  onDirtyChange?: (dirty: boolean) => void;
}) => {
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  return null;
};

/** A "See states" style link that lists names in a tooltip. */
const SeeList = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <ToolTip inline maxWidth="320px" text={<div className="columns-2 gap-x-[16px]">{children}</div>}>
    <button
      type="button"
      className="cursor-pointer whitespace-nowrap border-0 bg-transparent p-0 text-[12px] font-medium leading-[16px] text-primary-600 underline decoration-dotted underline-offset-[3px]"
    >
      {label}
    </button>
  </ToolTip>
);

/** Row label for a group of US states in the regions table. */
const RegionGroupLabel = ({
  label,
  list,
  description,
  overridden,
}: {
  label: string;
  list: React.ReactNode;
  description?: string;
  overridden?: boolean;
}) => (
  <span className="flex flex-col gap-[2px] py-[2px]">
    <span className="inline-flex flex-wrap items-center gap-[6px]">
      {label}
      {list}
    </span>
    {description && (
      <span className="max-w-[260px] whitespace-normal text-[12px] leading-[16px] text-neutral-600">
        {description}
      </span>
    )}
    {overridden && (
      <span className="text-[12px] leading-[16px] text-neutral-600">
        Some states are overridden below
      </span>
    )}
  </span>
);

const PreviewRegionButton = ({
  label,
  iconPath,
  enabled,
  disabledReason,
  onClick,
}: {
  label: string;
  iconPath: string;
  enabled: boolean;
  disabledReason: string;
  onClick: () => void;
}) => {
  const button = (
    <AquiloButton
      color="tertiary"
      customSizeClassname="h-[36px] text-[14px]"
      disabled={!enabled}
      onClick={onClick}
    >
      <img src={iconPath} alt="" width="20" height="14" className="mr-[8px] rounded-[2px]" />
      {label}
      <ArrowTopRightOnSquareIcon className="ml-[8px] h-[16px] w-[16px] text-neutral-500" />
    </AquiloButton>
  );

  return enabled ? (
    button
  ) : (
    <ToolTip inline text={disabledReason}>
      <span className="inline-flex">{button}</span>
    </ToolTip>
  );
};

export const ConsentBannerSettings = React.forwardRef<
  ConsentBannerSettingsHandle,
  ConsentBannerSettingsProps
>((props, ref) => {
  const { organization, environment } = props;
  const websiteDomain = websiteDomainFromPrivacyCenterUrl(props.privacyCenterURL);

  // The form's values for a set of saved settings: this environment's initial values, or the other environment's
  // settings when copying them in.
  const toFormValues = (settings: CMPSettingsDto): ConsentBannerFormValues => {
    return {
      usConsentManagementVariant: settings.usConsentManagementVariant,
      usConsentManagementVariantCA: settings.usConsentManagementVariantCA,
      [FLORIDA_CONSENT_MANAGEMENT_VARIANT_FIELD]: getFloridaConsentManagementVariant(
        settings.customConsentManagementVariants,
        settings.usConsentManagementVariantUSP,
      ),
      usConsentManagementVariantUSP: settings.usConsentManagementVariantUSP,
      usConsentManagementVariantNonUSP: settings.usConsentManagementVariantNonUSP,
      usConsentManagementVariantMinors: settings.usConsentManagementVariantMinors,
      canadaConsentManagementVariant: settings.canadaConsentManagementVariant,
      eeaukConsentManagementVariant: settings.eeaukConsentManagementVariant,
      customConsentManagementVariants: settings.customConsentManagementVariants,
      globalConsentManagementVariantDefault: settings.globalConsentManagementVariantDefault,
      globalConsentManagementVariants: settings.globalConsentManagementVariants,
      bannerIconColorPrimary: settings.bannerIconColorPrimary || ICON_PRIMARY_COLOR_DEFAULT,
      bannerIconColorSecondary: settings.bannerIconColorSecondary || ICON_SECONDARY_COLOR_DEFAULT,
      bannerBackgroundColor: settings.bannerBackgroundColor,
      bannerScreenPosition: settings.bannerScreenPosition,
      informationalBannerTitle:
        settings.informationalBannerTitle || INFORMATIONAL_BANNER_TITLE_DEFAULT_TEXT,
      informationalConsentManagementVariant:
        settings.informationalConsentManagementVariant === true,
      informationalBannerText: settings.informationalBannerText?.trim()
        ? settings.informationalBannerText
        : INFORMATIONAL_BANNER_DEFAULT_TEXT,
      informationalBannerAcceptButtonText:
        settings.informationalBannerAcceptButtonText ||
        INFORMATIONAL_BANNER_ACCEPT_BUTTON_DEFAULT_TEXT,
      informationalBannerDismissalStyle:
        settings.informationalBannerDismissalStyle ||
        InformationalBannerDismissalStyle.ACCEPT_BUTTON,
      bannerTextOptInEn: settings.bannerTextOptInEn,
      bannerTextOptOutEn: settings.bannerTextOptOutEn,
      bannerCopyVariant: settings.bannerCopyVariant || BannerCopyVariant.DEFAULT,
      bannerDismissalButtonCopyVariant:
        settings.bannerDismissalButtonCopyVariant || DismissalButtonCopyVariant.DEFAULT,
      bannerPreferencesDisclaimerTextEn: settings.bannerPreferencesDisclaimerTextEn,
      gdprConsentManagementEnabled: settings.gdprConsentManagementEnabled,
      previewURL: settings.previewURL ?? websiteDomain,
      consentChoiceRequiredBannerTypes: getInitialConsentChoiceRequiredBannerTypes(
        settings.consentChoiceRequiredBannerTypes,
        settings.consentRequiredMode,
      ),
      allowCSSOverrides: settings.allowCSSOverrides === true,
      // Not editable here, but carried so staging publishes (which replace the whole record) and copies keep it.
      usBannerVisibility: settings.usBannerVisibility ?? ConsentBannerUSVisibility.ALL,
    };
  };
  // Keeps the sticky header and "On this page" below any app-wide banner fixed over the page.
  const topBannerHeight = useTopBannerHeight();
  // The environment header's height grows when its mode bar wraps; sticky content and anchors sit below it.
  const environmentHeaderRef = useRef<HTMLDivElement>(null);
  const environmentHeaderHeight = useElementHeight(environmentHeaderRef);

  const showGDPRBannerSettings = organization?.featureGdpr;
  const showCanadaBannerSettings = organization?.featurePipeda;
  const showGlobalConsentSettings = organization?.featureGlobalConsent;
  const showPreferencesDisclaimerSettings = organization?.featureCmpPreferencesDisclaimer === true;
  const showMinorsConsentSettings =
    props.orgInfo?.showMinorsConsentSettingsUnder18 === true ||
    props.orgInfo?.showMinorsConsentSettingsUnder16 === true;
  const showCalifornia = !props.orgInfo?.showMinorsConsentSettingsUnder16; // If collecting from users under 16, CA is one of the Children states.

  // the youngest age we need to worry about for data collection consent (17 to account for New Jersey, if org is collecting under-18 but not under-16).
  const dataCollectionAge =
    props.orgInfo?.showMinorsConsentSettingsUnder16 === true
      ? 16
      : props.orgInfo?.showMinorsConsentSettingsUnder18 === true
      ? 17
      : null;

  // Toggle to show the custom consent management variants. We don't store this toggle's value - just the list of custom
  // variants - so it's stateful on the page (based on if there *are* any custom variants).
  const [showUSOverrides, setShowUSOverrides] = useState<boolean>(
    removeFloridaCustomConsentManagementVariant(props.customConsentManagementVariants).length > 0,
  );

  // Section-level toggle for the Global Consent block. On when there's any saved configuration; off otherwise.
  // Sync from props when they arrive (otherwise the initial state would be locked to the empty pre-fetch values).
  const hasGlobalConsentData =
    (props.globalConsentManagementVariantDefault != null &&
      props.globalConsentManagementVariantDefault !== ConsentManagementVariant.NONE) ||
    props.globalConsentManagementVariants?.length > 0;
  const [showGlobalConsent, setShowGlobalConsent] = useState<boolean>(hasGlobalConsentData);
  useEffect(() => {
    if (hasGlobalConsentData) {
      setShowGlobalConsent(true);
    }
  }, [hasGlobalConsentData]);

  // Transient flag for the Global Consent overrides editor: true after the user clicks "Add country",
  // so the table can render with an empty starter row even though no overrides are saved yet. The
  // saved-overrides path (length > 0) drives visibility on its own, so this only matters in the
  // empty-but-editing window.
  const [isAddingGlobalOverride, setIsAddingGlobalOverride] = useState<boolean>(false);

  const [loadingPreview, setLoadingPreview] = useState<boolean>(false);
  const [loadingPreviewProgress, setLoadingPreviewProgress] = useState<string>("0%"); // Make the preview waiting feel faster by showing a percent
  const [previewReady, setPreviewReady] = useState<boolean>(false); // Preview is ready, show a button to launch it. Two effects: additional imperceptible delay to allow for even more time to publish, AND avoids "popup blocked" since it's a direct user click instead of delayed window.open.
  const [bannerPreviewURL, setBannerPreviewURL] = useState<string>(""); // The preview URL we need to shuttle out of the form vals to the preview launch button.

  // Switching environments or copying settings in would throw away unpublished edits, so ask first.
  const [pendingEnvironment, setPendingEnvironment] = useState<ConsentEnvironment | null>(null);
  const [confirmCopyDialogOpen, setConfirmCopyDialogOpen] = useState(false);
  // Bumped when settings are copied in, so rows that keep their own state from the values they mounted with remount.
  const [copiedSettingsVersion, setCopiedSettingsVersion] = useState(0);

  const [confirmClearUSCustomRegionsDialogOpen, setConfirmClearUSCustomRegionsDialogOpen] =
    useState(false);
  const [confirmClearGlobalConsentDialogOpen, setConfirmClearGlobalConsentDialogOpen] =
    useState(false);

  // Ref to the form so we can expose submitting it to parent components
  const formRef = React.useRef<FormApi<ConsentBannerFormValues> | null>(null);

  useImperativeHandle(ref, () => ({
    submitForm: async () => {
      if (!formRef.current) {
        return false;
      }
      const submitErrors = await formRef.current.submit();
      // `submit()` returns nothing when validation stops it, so also check that the publish went through.
      const { submitSucceeded, submitFailed } = formRef.current.getState();
      return !submitErrors && submitSucceeded && !submitFailed;
    },
    differsFrom: (settings) =>
      formRef.current != null &&
      differingSettings(formRef.current.getState().values, toFormValues(settings)).length > 0,
    getPublishedSettings: () =>
      formRef.current ? toPublishedSettings(formRef.current.getState().values) : null,
  }));

  const orgId = usePrimaryOrganizationId();
  const [cookieScan] = useCookieScanResult(orgId);
  const hasCookies = cookieScan?.cookies.length > 0;
  const enqueueSnackbar = useSnackBar();

  // Regions that need a banner chosen before either environment can publish.
  const shownBannerRegions: ShownBannerRegions = {
    california: showCalifornia,
    childrenConsentStates: showMinorsConsentSettings,
    canada: showCanadaBannerSettings === true,
    eeaUkCh: showGDPRBannerSettings === true,
    globalConsent: showGlobalConsentSettings === true && showGlobalConsent,
  };
  const globalConsentTurnedOff = showGlobalConsentSettings === true && !showGlobalConsent;

  // The settings to send for a production or staging publish.
  const toPublishedSettings = (formVals: ConsentBannerFormValues): CMPSettingsDto => {
    const cmpSettings = normalizeFloridaConsentManagementVariant(
      withClearedSettings(formVals, { globalConsentTurnedOff, saved: toFormValues(props) }),
    );
    cmpSettings.previewForceGeoCountry = null; // Publishing, clear out the preview settings
    cmpSettings.previewForceGeoRegion = null;
    cmpSettings.isStagingPublish = environment === "staging";

    setLegacyConsentRequiredMode(cmpSettings);
    return cmpSettings;
  };

  const publishPreview = async (
    previewForLocation: ProcessingRegion,
    formVals: ConsentBannerFormValues,
  ) => {
    const cmpSettings = normalizeFloridaConsentManagementVariant(
      withClearedSettings(formVals, { globalConsentTurnedOff }),
    );

    if (!cmpSettings.previewURL) {
      enqueueSnackbar("Add a preview page before previewing your banner.", {
        variant: "error",
      });
      return;
    }

    setBannerPreviewURL(cmpSettings.previewURL);
    setLoadingPreview(true);

    switch (previewForLocation) {
      case "UNITED_STATES":
        cmpSettings.previewForceGeoCountry = "US";
        cmpSettings.previewForceGeoRegion = "CA";
        break;
      case "EEA_UK":
        cmpSettings.previewForceGeoCountry = "UK";
        cmpSettings.previewForceGeoRegion = "";
        break;
      case "CANADA":
        cmpSettings.previewForceGeoCountry = "CA";
        cmpSettings.previewForceGeoRegion = "";
        break;
    }

    setLegacyConsentRequiredMode(cmpSettings);

    props.onUpdateCMPCustomization(cmpSettings);
    for (let i = 0; i < LOADING_PREVIEW_DELAY_SECONDS; i++) {
      // Publish is pretty fast but not instant. Add some random noise so it's not totally obvious.
      setLoadingPreviewProgress(
        Math.max(
          0,
          Math.round((i * 100) / LOADING_PREVIEW_DELAY_SECONDS) +
            (Math.round(Math.random() * 7) - 3),
        ) + "%",
      );
      await sleep(1000);
    }

    setPreviewReady(true);
  };

  const copyOtherEnvironmentSettings = (form: FormApi<ConsentBannerFormValues>) => {
    if (!props.otherEnvironmentSettings) {
      return;
    }
    const copiedValues = toFormValues(props.otherEnvironmentSettings);
    form.batch(() => {
      (Object.keys(copiedValues) as (keyof ConsentBannerFormValues)[]).forEach((field) => {
        // Keep this environment's preview page; it usually points at a different site.
        if (field !== "previewURL") {
          form.change(field, copiedValues[field]);
        }
      });
    });
    setShowUSOverrides(
      removeFloridaCustomConsentManagementVariant(copiedValues.customConsentManagementVariants)
        .length > 0,
    );
    setShowGlobalConsent(
      (copiedValues.globalConsentManagementVariantDefault != null &&
        copiedValues.globalConsentManagementVariantDefault !== ConsentManagementVariant.NONE) ||
        copiedValues.globalConsentManagementVariants?.length > 0,
    );
    setIsAddingGlobalOverride(false);
    setCopiedSettingsVersion((version) => version + 1);
    enqueueSnackbar(
      environment === "production" ? "Copied staging settings" : "Copied production settings",
      {
        variant: "success",
        body:
          environment === "production"
            ? "Publish to production to make them live."
            : "Publish to staging to try them on your staging site.",
      },
    );
  };

  const openPreview = () => {
    window.open(`https://${bannerPreviewURL}#polaris_preview=true`, "_blank");
    enqueueSnackbar("Preview opened in a new tab", { variant: "success" });
    setLoadingPreview(false);
    setPreviewReady(false);
  };

  const regionNames = (
    include: (region: OverrideableRegion) => boolean,
    customVariantsUS?: CustomConsentManagementVariant[],
    showAge = false,
  ) =>
    OVERRIDEABLE_REGIONS.filter(include).map((region) => (
      <span
        key={region.region}
        className={clsx(
          "block",
          customVariantsUS?.some((customRegion) => customRegion.region == region.region) &&
            "text-gray-400",
        )}
      >
        {region.label}
        {showAge && region.dataCollectionAge !== 18 ? ` (under ${region.dataCollectionAge})` : null}
      </span>
    ));

  const hasOverriddenRegion = (
    include: (region: OverrideableRegion) => boolean,
    customVariantsUS?: CustomConsentManagementVariant[],
  ) =>
    customVariantsUS?.some((item) =>
      OVERRIDEABLE_REGIONS.some((state) => include(state) && state.region == item.region),
    );

  /**
   * The row label and "See states" hover for the US consent rows.
   * @param fieldName The name of the field to map to a label.
   * @param customVariantsUS The custom variants US list to check for overrides to indicate.
   */
  const fieldDescriptionToRowLabelProvider = (
    fieldName: string,
    customVariantsUS?: CustomConsentManagementVariant[],
  ): React.ReactNode => {
    const isPrivacyState = (region: OverrideableRegion) =>
      region.isUSP &&
      region.region !== FLORIDA_REGION &&
      !(region.isChildrenConsent && showMinorsConsentSettings);
    const isNonPrivacyState = (region: OverrideableRegion) =>
      region.isNonUSP && !(region.isChildrenConsent && showMinorsConsentSettings);
    const isChildrenConsentState = (region: OverrideableRegion) =>
      region.isChildrenConsent && region.dataCollectionAge >= dataCollectionAge;

    switch (fieldName) {
      case CALIFORNIA_CONSENT_MANAGEMENT_VARIANT_FIELD:
        return "California";
      case FLORIDA_CONSENT_MANAGEMENT_VARIANT_FIELD:
        return (
          <span className="inline-flex items-center gap-[6px]">
            Florida
            <ToolTip
              inline
              text="California and Florida currently present a significantly higher risk of wiretap litigation."
              maxWidth={"400px"}
            >
              <InformationCircleIcon
                className="h-[16px] w-[16px] cursor-pointer text-neutral-500"
                aria-label="Why Florida is listed separately"
              />
            </ToolTip>
          </span>
        );
      case PRIVACY_STATES_CONSENT_MANAGEMENT_VARIANT_FIELD:
        return (
          <RegionGroupLabel
            label="Privacy states"
            list={
              <SeeList label="See states">{regionNames(isPrivacyState, customVariantsUS)}</SeeList>
            }
            overridden={hasOverriddenRegion(isPrivacyState, customVariantsUS)}
          />
        );
      case NON_PRIVACY_STATES_CONSENT_MANAGEMENT_VARIANT_FIELD:
        return (
          <RegionGroupLabel
            label="Non-privacy states"
            list={
              <SeeList label="See states">
                {regionNames(isNonPrivacyState, customVariantsUS)}
              </SeeList>
            }
            overridden={hasOverriddenRegion(isNonPrivacyState, customVariantsUS)}
          />
        );
      case CHILDREN_CONSENT_MANAGEMENT_VARIANT_FIELD:
        return (
          <RegionGroupLabel
            label={`Children consent states (under ${
              props.orgInfo?.showMinorsConsentSettingsUnder16 === true ? "16" : "18"
            })`}
            list={
              <SeeList label="See states">
                {regionNames(isChildrenConsentState, customVariantsUS, true)}
              </SeeList>
            }
            description="Because you collect children's data, some states require you to collect consent."
            overridden={hasOverriddenRegion((state) => state.isChildrenConsent, customVariantsUS)}
          />
        );
    }
  };

  return (
    <>
      <Form
        // The settings data is refetched in the background (e.g. the periodic /users/self/session
        // poll re-renders the tree), which hands react-final-form a fresh `initialValues` object with
        // new array/object references but identical values. The default shallow comparison treats that
        // as a change and reinitializes the form, silently discarding the user's in-progress edits
        // (POL-7562). A deep comparison only reinitializes when the saved config actually changes —
        // preserving edits across no-op refetches while still resetting to pristine after a publish.
        initialValuesEqual={_.isEqual}
        initialValues={toFormValues(props)}
        // Publishes to the environment being edited: production or staging.
        onSubmit={async (values) => {
          // Don't publish without an explicit choice for every banner region shown.
          const missingBanners = regionsWithoutABanner(values, shownBannerRegions);
          if (missingBanners.length !== 0) {
            const message = `Choose a banner for ${lexicalJoin(missingBanners)} before publishing.`;
            enqueueSnackbar(message, { variant: "error" });

            return { [FORM_ERROR]: message };
          }

          try {
            await props.onUpdateCMPCustomization(toPublishedSettings(values));
          } catch (error) {
            if (environment === "staging") {
              enqueueSnackbar("Couldn't publish to staging. Try again.", { variant: "error" });
              return { [FORM_ERROR]: "Couldn't publish to staging" };
            }
            enqueueSnackbar("Failed to publish consent banner settings. Please try again.", {
              variant: "error",
            });
            return { [FORM_ERROR]: "Failed to publish consent banner settings" };
          }

          if (environment === "staging") {
            enqueueSnackbar("Published to staging", {
              variant: "success",
              body: "It can take a few minutes for changes to appear on your staging site.",
            });
          } else if (props.isConfirmedCustomizableCMPVersion) {
            enqueueSnackbar("Published to production", { variant: "success" });
          } else {
            enqueueSnackbar("Published to production", {
              variant: "warning",
              body: "We still haven't been able to verify that your site is using the latest Polaris JavaScript. The latest JavaScript is required for the changes to be visible.",
            });
          }
        }}
        validate={(values) => {
          const errors = {};
          requiredString(values, "informationalBannerText", errors);
          requiredString(values, "informationalBannerAcceptButtonText", errors);
          return errors;
        }}
        render={({ handleSubmit, form, dirty, submitting, hasValidationErrors, values }) => {
          // Store the form in the ref so parent can submit it
          formRef.current = form;

          const usVariantCA = form.getFieldState(
            CALIFORNIA_CONSENT_MANAGEMENT_VARIANT_FIELD,
          )?.value;
          const usVariantFL = form.getFieldState(FLORIDA_CONSENT_MANAGEMENT_VARIANT_FIELD)?.value;
          const usVariantUSP = form.getFieldState(
            PRIVACY_STATES_CONSENT_MANAGEMENT_VARIANT_FIELD,
          )?.value;
          const usVariantNonUSP = form.getFieldState(
            NON_PRIVACY_STATES_CONSENT_MANAGEMENT_VARIANT_FIELD,
          )?.value;
          const usVariantMinors = form.getFieldState(
            CHILDREN_CONSENT_MANAGEMENT_VARIANT_FIELD,
          )?.value;
          const customVariantsUS: CustomConsentManagementVariant[] = form.getFieldState(
            "customConsentManagementVariants",
          )?.value;
          const customVariantsUSWithoutFlorida =
            removeFloridaCustomConsentManagementVariant(customVariantsUS);

          const hasUSInformationalBanner =
            [usVariantCA, usVariantFL, usVariantUSP, usVariantNonUSP, usVariantMinors].some(
              (value) => value === ConsentManagementVariant.INFORMATIONAL_ONLY,
            ) ||
            customVariantsUSWithoutFlorida?.some(
              (item) => item.variant === ConsentManagementVariant.INFORMATIONAL_ONLY,
            );
          const hasUSOptOutBanner =
            [usVariantCA, usVariantFL, usVariantUSP, usVariantNonUSP, usVariantMinors].some(
              (value) => value === ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT,
            ) ||
            customVariantsUSWithoutFlorida?.some(
              (item) => item.variant === ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT,
            );

          const hasUSOptInBanner =
            [usVariantCA, usVariantFL, usVariantUSP, usVariantNonUSP, usVariantMinors].some(
              (value) => value === ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT,
            ) ||
            customVariantsUSWithoutFlorida?.some(
              (item) => item.variant === ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT,
            );

          const hasUSInteractiveBanner =
            [usVariantCA, usVariantFL, usVariantUSP, usVariantNonUSP, usVariantMinors].some(
              (value) =>
                [
                  ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT,
                  ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT,
                ].includes(value),
            ) ||
            customVariantsUSWithoutFlorida?.some(
              (item) =>
                item.variant === ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT ||
                item.variant === ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT,
            );

          const usBannerEnabled =
            [usVariantCA, usVariantFL, usVariantUSP, usVariantNonUSP, usVariantMinors].some(
              (value) => value && value !== ConsentManagementVariant.NONE,
            ) ||
            customVariantsUSWithoutFlorida.some(
              (item) => item.variant && item.variant !== ConsentManagementVariant.NONE,
            );

          const customVariantsUSAllowedRegions = getAdvancedUSOverrideRegions();

          const handleCustomVariantsUpdate = (
            updatedVariants: CustomConsentManagementVariant[],
          ) => {
            form.change(
              "customConsentManagementVariants",
              applyFloridaCustomConsentManagementVariant(updatedVariants, usVariantFL),
            );
          };

          const canadaVariant = form.getFieldState("canadaConsentManagementVariant")?.value;
          const eeaukVariant = form.getFieldState("eeaukConsentManagementVariant")?.value;
          const globalDefaultVariant = form.getFieldState(
            "globalConsentManagementVariantDefault",
          )?.value;
          const globalCustomVariants: CustomConsentManagementVariant[] = form.getFieldState(
            "globalConsentManagementVariants",
          )?.value;
          // Show the overrides table when there's a saved override OR the user is mid-add. Otherwise
          // collapse the empty table down to a single "Add country" affordance.
          const showGlobalOverridesTable =
            globalCustomVariants?.length > 0 || isAddingGlobalOverride;

          const canadaBannerEnabled =
            showCanadaBannerSettings && canadaVariant && canadaVariant !== "NONE";
          const eeaukBannerEnabled =
            showGDPRBannerSettings && eeaukVariant && eeaukVariant !== "NONE";
          const globalConsentBannerEnabled =
            showGlobalConsentSettings &&
            showGlobalConsent &&
            ((globalDefaultVariant && globalDefaultVariant !== ConsentManagementVariant.NONE) ||
              globalCustomVariants?.some((item) => item.variant !== ConsentManagementVariant.NONE));
          const showBannerVisualSettings =
            usBannerEnabled ||
            canadaBannerEnabled ||
            eeaukBannerEnabled ||
            globalConsentBannerEnabled;

          const hasOptInBanner =
            canadaBannerEnabled ||
            [usVariantCA, usVariantFL, usVariantUSP, usVariantNonUSP].some(
              (value) => value === ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT,
            ) ||
            customVariantsUSWithoutFlorida?.some(
              (item) => item.variant === ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT,
            ) ||
            (globalConsentBannerEnabled &&
              (globalDefaultVariant === ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT ||
                globalCustomVariants?.some(
                  (item) =>
                    item.variant === ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT,
                )));
          const hasOptOutBanner =
            canadaBannerEnabled ||
            [usVariantCA, usVariantFL, usVariantUSP, usVariantNonUSP].some(
              (value) => value === ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT,
            ) ||
            customVariantsUSWithoutFlorida?.some(
              (item) => item.variant === ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT,
            ) ||
            (globalConsentBannerEnabled &&
              (globalDefaultVariant === ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT ||
                globalCustomVariants?.some(
                  (item) => item.variant === ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT,
                )));
          const hasDismissButtonTextBanner =
            hasUSOptInBanner ||
            hasUSOptOutBanner ||
            canadaVariant === ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT ||
            canadaVariant === ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT ||
            (globalConsentBannerEnabled &&
              (globalDefaultVariant === ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT ||
                globalDefaultVariant === ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT ||
                globalCustomVariants?.some(
                  (item) =>
                    item.variant === ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT ||
                    item.variant === ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT,
                )));

          const showUSCallout = !hasCookies && hasUSInteractiveBanner;
          const showCanadaCallout =
            (canadaVariant === ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT ||
              canadaVariant === ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT) &&
            !hasCookies;
          const showEEAUKCallout =
            (eeaukVariant === ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT ||
              eeaukVariant === ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT) &&
            !hasCookies;

          const handleGlobalCustomVariantsUpdate = (
            updatedVariants: CustomConsentManagementVariant[],
          ) => {
            form.change("globalConsentManagementVariants", updatedVariants);
            // Removing the last override returns the editor to its empty steady state — show the
            // "Add country" button instead of an empty table on next render.
            if (updatedVariants.length === 0) {
              setIsAddingGlobalOverride(false);
            }
          };

          // Which regions show a given banner type, for the "Used in ..." line on each text group.
          const usedInText = (variant: ConsentManagementVariant): string | undefined => {
            const names: string[] = [];
            if (showCalifornia && usVariantCA === variant) names.push("California");
            if (usVariantFL === variant) names.push("Florida");
            if (showMinorsConsentSettings && usVariantMinors === variant) {
              names.push("children consent states");
            }
            if (usVariantUSP === variant) names.push("privacy states");
            if (usVariantNonUSP === variant) names.push("non-privacy states");
            if (customVariantsUSWithoutFlorida?.some((item) => item.variant === variant)) {
              names.push("custom states");
            }
            // The Canada banner is opt-in for visitors in Quebec and opt-out for the rest of Canada.
            if (canadaBannerEnabled) {
              if (variant === ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT) {
                names.push("Quebec");
              } else if (variant === ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT) {
                names.push("Canada outside Quebec");
              }
            }
            if (
              globalConsentBannerEnabled &&
              (globalDefaultVariant === variant ||
                globalCustomVariants?.some((item) => item.variant === variant))
            ) {
              names.push("global consent countries");
            }
            return names.length > 0 ? `Used in ${lexicalJoin(names)}` : undefined;
          };

          // The accept button text is shared by the opt-out and informational banners; show it with whichever is on.
          const showAcceptButtonRow = hasUSInformationalBanner || hasUSOptOutBanner;
          const acceptButtonInOptOutGroup = showAcceptButtonRow && hasOptOutBanner;
          const acceptButtonInInformationalGroup =
            showAcceptButtonRow && !hasOptOutBanner && hasUSInformationalBanner;
          const acceptButtonHelpText =
            hasOptOutBanner && hasUSInformationalBanner
              ? "The button that dismisses the opt-out banner and the informational banner."
              : hasUSInformationalBanner
              ? "The button that dismisses the informational banner."
              : "The button that dismisses the opt-out banner.";

          const showAppearanceSection =
            hasUSInteractiveBanner ||
            canadaBannerEnabled ||
            eeaukBannerEnabled ||
            globalConsentBannerEnabled;
          const showTextSection =
            hasUSInteractiveBanner ||
            hasOptInBanner ||
            hasOptOutBanner ||
            hasUSInformationalBanner ||
            hasDismissButtonTextBanner;
          // Region previews run on the production script only; staging is tested on the staging site itself.
          const showPreviewSection = showBannerVisualSettings && environment === "production";

          const requestEnvironmentChange = (nextEnvironment: ConsentEnvironment) => {
            if (nextEnvironment === environment) {
              return;
            }
            if (dirty) {
              setPendingEnvironment(nextEnvironment);
            } else {
              props.onEnvironmentChange(nextEnvironment);
            }
          };

          // Staging copies production's settings to start fresh; production copies staging's to promote them.
          const otherEnvironment: ConsentEnvironment =
            environment === "production" ? "staging" : "production";
          const canCopyOtherEnvironment =
            !props.loadFailed &&
            props.otherEnvironmentSettings != null &&
            (environment === "production" || props.hasSavedSettings);
          const requestCopy = () => {
            if (dirty) {
              setConfirmCopyDialogOpen(true);
            } else {
              copyOtherEnvironmentSettings(form);
            }
          };

          // Compares saved settings only, so it doesn't change while editing. Only shown when the other environment's
          // settings can be copied in, since that's the way to bring the two back in line.
          const differingSettingNames =
            canCopyOtherEnvironment && props.otherEnvironmentSettings
              ? differingSettings(
                  form.getState().initialValues,
                  toFormValues(props.otherEnvironmentSettings),
                ).map((field) => ENVIRONMENT_DIFF_SETTING_LABELS[field] ?? _.startCase(field))
              : [];
          const differingSettingsCount = differingSettingNames.length;
          const modeBarAction = (differingSettingsCount > 0 || canCopyOtherEnvironment) && (
            <>
              {differingSettingsCount > 0 && (
                <ToolTip
                  inline
                  maxWidth="320px"
                  text={
                    <ul className="m-0 list-none p-0">
                      {differingSettingNames.map((name) => (
                        <li key={name}>{name}</li>
                      ))}
                    </ul>
                  }
                >
                  <button
                    type="button"
                    className="cursor-help whitespace-nowrap border-0 bg-transparent p-0 text-current underline decoration-dotted underline-offset-[3px]"
                  >
                    {differingSettingsCount === 1
                      ? "1 setting differs"
                      : `${differingSettingsCount} settings differ`}{" "}
                    from {otherEnvironment}
                  </button>
                </ToolTip>
              )}
              {canCopyOtherEnvironment && (
                <button
                  type="button"
                  onClick={requestCopy}
                  className="cursor-pointer whitespace-nowrap border-0 bg-transparent p-0 font-medium text-current underline underline-offset-2"
                >
                  Copy {otherEnvironment} settings
                </button>
              )}
            </>
          );

          const handlePublish = () => {
            if (hasValidationErrors) {
              enqueueSnackbar("Fill in the required banner text before publishing", {
                variant: "error",
                body: "The informational banner text and the accept button text can't be empty.",
              });
              return;
            }
            handleSubmit();
          };

          const enabledRegionNames = [
            usBannerEnabled && "US",
            canadaBannerEnabled && "Canada",
            eeaukBannerEnabled && "EEA/UK/CH",
            globalConsentBannerEnabled && "Global",
          ].filter(Boolean) as string[];
          const backgroundLabel =
            values.bannerBackgroundColor === "#222222"
              ? "Dark"
              : values.bannerBackgroundColor === "#eeeeee"
              ? "Light"
              : null;
          const positionLabel = values.bannerScreenPosition
            ? SCREEN_POSITION_LABELS[values.bannerScreenPosition]
            : null;
          // Banner text rows on the page whose value isn't the default.
          const isCustomized = (value: string | undefined, defaultValue: string) =>
            Boolean(value?.trim()) && value.trim() !== defaultValue.trim();
          const customTextCount = [
            hasUSInteractiveBanner &&
              isCustomized(
                values.informationalBannerTitle,
                INFORMATIONAL_BANNER_TITLE_DEFAULT_TEXT,
              ),
            hasOptInBanner && Boolean(values.bannerTextOptInEn?.trim()),
            hasOptInBanner &&
              values.bannerCopyVariant != null &&
              values.bannerCopyVariant !== BannerCopyVariant.DEFAULT,
            hasOptOutBanner && Boolean(values.bannerTextOptOutEn?.trim()),
            showAcceptButtonRow &&
              isCustomized(
                values.informationalBannerAcceptButtonText,
                INFORMATIONAL_BANNER_ACCEPT_BUTTON_DEFAULT_TEXT,
              ),
            hasUSInformationalBanner &&
              isCustomized(values.informationalBannerText, INFORMATIONAL_BANNER_DEFAULT_TEXT) &&
              !isKnownInformationalBannerDefaultText(values.informationalBannerText),
            hasDismissButtonTextBanner &&
              values.bannerDismissalButtonCopyVariant != null &&
              values.bannerDismissalButtonCopyVariant !== DismissalButtonCopyVariant.DEFAULT,
            hasDismissButtonTextBanner &&
              showPreferencesDisclaimerSettings &&
              Boolean(values.bannerPreferencesDisclaimerTextEn?.trim()),
          ].filter(Boolean).length;
          const advancedCount =
            (values.consentChoiceRequiredBannerTypes?.length > 0 ? 1 : 0) +
            (values.allowCSSOverrides ? 1 : 0);

          const onThisPageItems: ConsentOnThisPageItem[] = [
            {
              id: "consent-regions",
              title: "Regions",
              summary:
                enabledRegionNames.length > 0 ? enabledRegionNames.join(", ") : "No banners on",
            },
            ...(showAppearanceSection
              ? [
                  {
                    id: "consent-appearance",
                    title: "Appearance",
                    summary:
                      [backgroundLabel, positionLabel].filter(Boolean).join(" · ") || undefined,
                  },
                ]
              : []),
            ...(showTextSection
              ? [
                  {
                    id: "consent-text",
                    title: "Banner text",
                    summary:
                      customTextCount === 0
                        ? "Default text"
                        : customTextCount === 1
                        ? "1 custom text"
                        : `${customTextCount} custom texts`,
                  },
                ]
              : []),
            ...(showBannerVisualSettings
              ? [
                  {
                    id: "consent-advanced",
                    title: "Advanced options",
                    summary:
                      advancedCount === 0 ? "Nothing turned on" : `${advancedCount} turned on`,
                  },
                ]
              : []),
            ...(showPreviewSection
              ? [
                  {
                    id: "consent-preview",
                    title: "Preview",
                    summary: values.previewURL || "No page set",
                  },
                ]
              : []),
          ];

          const previewContent = loadingPreview ? (
            previewReady ? (
              <div className="flex flex-wrap items-center gap-[12px]">
                <span className="text-[14px] leading-[20px] text-neutral-800">
                  Your preview is ready.
                </span>
                <AquiloButton
                  color="primary"
                  customSizeClassname="h-[36px] text-[14px]"
                  onClick={openPreview}
                >
                  View preview
                  <ArrowTopRightOnSquareIcon className="ml-[8px] h-[16px] w-[16px]" />
                </AquiloButton>
              </div>
            ) : (
              <div role="status" className="flex w-full max-w-[440px] items-center gap-[12px]">
                <div className="h-[6px] flex-1 overflow-hidden rounded-full bg-neutral-200">
                  <div
                    className="h-full rounded-full bg-primary-600 transition-[width] duration-300"
                    style={{ width: loadingPreviewProgress }}
                  />
                </div>
                <span className="text-[13px] leading-[18px] tabular-nums text-neutral-700">
                  Preparing preview {loadingPreviewProgress}
                </span>
              </div>
            )
          ) : (
            <div className="flex flex-wrap gap-[8px]">
              <PreviewRegionButton
                label="US (California)"
                iconPath="/assets/images/flag-usa.svg"
                enabled={usBannerEnabled}
                disabledReason="Turn on a US banner to preview it"
                onClick={() => publishPreview("UNITED_STATES", form.getState().values)}
              />
              {showCanadaBannerSettings && (
                <PreviewRegionButton
                  label="Canada"
                  iconPath="/assets/images/flag-canada.svg"
                  enabled={Boolean(canadaBannerEnabled)}
                  disabledReason="Turn on the Canada banner to preview it"
                  onClick={() => publishPreview("CANADA", form.getState().values)}
                />
              )}
              {showGDPRBannerSettings && (
                <PreviewRegionButton
                  label="EEA, UK, and Switzerland"
                  iconPath="/assets/images/flag-eea.svg"
                  enabled={Boolean(eeaukBannerEnabled)}
                  disabledReason="Turn on the EEA, UK, and Switzerland banner to preview it"
                  onClick={() => publishPreview("EEA_UK", form.getState().values)}
                />
              )}
            </div>
          );

          return (
            <>
              {confirmClearUSCustomRegionsDialogOpen && (
                <Modal
                  title="Clear custom US states?"
                  body="This clears the consent settings for every custom US state."
                  alignText="left"
                  alignButton="right"
                  closeButton={true}
                  onClose={() => setConfirmClearUSCustomRegionsDialogOpen(false)}
                  firstButton="Cancel"
                  firstButtonColor="tertiary"
                  onFirstButtonClick={() => setConfirmClearUSCustomRegionsDialogOpen(false)}
                  secondButton="Clear states"
                  secondButtonColor="primary"
                  onSecondButtonClick={() => {
                    form.change(
                      "customConsentManagementVariants",
                      applyFloridaCustomConsentManagementVariant([], usVariantFL),
                    );
                    setShowUSOverrides(false);
                    setConfirmClearUSCustomRegionsDialogOpen(false);
                  }}
                  showOverlay={true}
                  closeOnOverlayClick={false}
                />
              )}
              {pendingEnvironment && (
                <Modal
                  title="Discard unpublished changes?"
                  body={`Your changes to ${environment} aren't published. Switching to ${pendingEnvironment} discards them.`}
                  alignText="left"
                  alignButton="right"
                  closeButton={true}
                  onClose={() => setPendingEnvironment(null)}
                  firstButton="Keep editing"
                  firstButtonColor="tertiary"
                  onFirstButtonClick={() => setPendingEnvironment(null)}
                  secondButton="Discard changes"
                  secondButtonColor="primary"
                  onSecondButtonClick={() => {
                    props.onEnvironmentChange(pendingEnvironment);
                    setPendingEnvironment(null);
                  }}
                  showOverlay={true}
                  closeOnOverlayClick={false}
                />
              )}
              {confirmCopyDialogOpen && (
                <Modal
                  title="Replace your unpublished changes?"
                  body={`Copying the ${otherEnvironment} settings replaces the changes you haven't published.`}
                  alignText="left"
                  alignButton="right"
                  closeButton={true}
                  onClose={() => setConfirmCopyDialogOpen(false)}
                  firstButton="Cancel"
                  firstButtonColor="tertiary"
                  onFirstButtonClick={() => setConfirmCopyDialogOpen(false)}
                  secondButton="Copy settings"
                  secondButtonColor="primary"
                  onSecondButtonClick={() => {
                    copyOtherEnvironmentSettings(form);
                    setConfirmCopyDialogOpen(false);
                  }}
                  showOverlay={true}
                  closeOnOverlayClick={false}
                />
              )}
              {confirmClearGlobalConsentDialogOpen && (
                <Modal
                  title="Turn off global consent?"
                  body="This clears all global consent banner settings."
                  alignText="left"
                  alignButton="right"
                  closeButton={true}
                  onClose={() => setConfirmClearGlobalConsentDialogOpen(false)}
                  firstButton="Cancel"
                  firstButtonColor="tertiary"
                  onFirstButtonClick={() => setConfirmClearGlobalConsentDialogOpen(false)}
                  secondButton="Turn off"
                  secondButtonColor="primary"
                  onSecondButtonClick={() => {
                    form.change(
                      "globalConsentManagementVariantDefault",
                      ConsentManagementVariant.NONE,
                    );
                    form.change("globalConsentManagementVariants", []);
                    setShowGlobalConsent(false);
                    setIsAddingGlobalOverride(false);
                    setConfirmClearGlobalConsentDialogOpen(false);
                  }}
                  showOverlay={true}
                  closeOnOverlayClick={false}
                />
              )}

              <ReportDirty dirty={dirty} onDirtyChange={props.onDirtyChange} />
              <div className="tw-boundary">
                <div
                  style={
                    {
                      "--consent-sticky-top": `${topBannerHeight}px`,
                      "--consent-header-height": `${environmentHeaderHeight}px`,
                    } as React.CSSProperties
                  }
                >
                  <ConsentEnvironmentHeader
                    ref={environmentHeaderRef}
                    environment={environment}
                    onEnvironmentChange={requestEnvironmentChange}
                    autoFocusSwitcher={props.focusEnvironmentSwitcher}
                    websiteDomain={websiteDomain}
                    hasUnpublishedChanges={dirty}
                    hasSavedSettings={props.hasSavedSettings}
                    loadFailed={props.loadFailed}
                    isPublishing={submitting}
                    canPublish={
                      !props.loadFailed &&
                      // Staging can always be republished, e.g. to rebuild its script.
                      (environment === "staging" || (dirty && !loadingPreview))
                    }
                    onPublish={handlePublish}
                    modeBarAction={modeBarAction}
                  />

                  <div className="mt-[24px] grid grid-cols-[200px_minmax(0,1fr)] items-start gap-[24px]">
                    <ConsentOnThisPage
                      items={onThisPageItems}
                      topOffset={topBannerHeight + environmentHeaderHeight}
                    />

                    <div className="flex min-w-0 flex-col gap-[24px]">
                      {props.intro}

                      <ConsentSection
                        id="consent-regions"
                        title="Regions"
                        description="Choose which banner visitors see, based on where they're browsing from."
                      >
                        <ConsentManagementVariantRow
                          title="United States"
                          helpText="US privacy laws do not require a cookie banner, but many businesses choose to offer consent choices for various reasons, including to minimize data privacy risks."
                          fields={getUSBannerConsentManagementVariantFields(
                            showCalifornia,
                            showMinorsConsentSettings,
                          )}
                          allowInformationalBanner={true}
                          allowOptOutBanner={true}
                          allowOptInBanner={true}
                          iconPath="/assets/images/flag-usa.svg"
                          showTooltip={true}
                          fieldToLabel={FORM_FIELDS_TO_HUMAN_DESCRIPTION}
                          reverseOptionOrder
                          fieldDescriptionToRowLabelProvider={(field: string) =>
                            fieldDescriptionToRowLabelProvider(
                              field,
                              customVariantsUSWithoutFlorida,
                            )
                          }
                          contentBeforeTable={
                            <>
                              <ConsentNote>
                                California and Florida are seeing increased website wiretapping
                                litigation. This{" "}
                                <WiretappingNoticeLink href={WIRETAPPING_LITIGATION_MAP_URL}>
                                  wiretap litigation map
                                </WiretappingNoticeLink>{" "}
                                shows a state-by-state breakdown of cases filed.{" "}
                                <WiretappingNoticeLink href={CIPA_LEARN_MORE_URL}>
                                  Learn more about CIPA
                                </WiretappingNoticeLink>
                                .
                              </ConsentNote>
                              {hasUSOptInBanner && (
                                <ConsentNote>
                                  Opt-In Enabled: Non-essential cookies and scripts are turned off
                                  by default with an opt-in banner. You may notice a drop in
                                  measurements, such as website analytics.
                                </ConsentNote>
                              )}
                            </>
                          }
                          contentAfterTable={
                            <>
                              {hasUSInformationalBanner && (
                                <p className="m-0 max-w-[68ch] text-[13px] leading-[18px] text-neutral-600">
                                  Note that an informational privacy banner will not affect the use
                                  of cookies or trackers on your site, and should not be used as a
                                  replacement for collecting affirmative consent from visitors where
                                  that is required.
                                </p>
                              )}
                              <div className="flex w-full items-start gap-[12px] pt-[4px]">
                                <Toggle
                                  checked={showUSOverrides}
                                  label="Advanced Banner Visibility Options"
                                  onChange={(checked) => {
                                    if (!checked && customVariantsUSWithoutFlorida?.length > 0) {
                                      setConfirmClearUSCustomRegionsDialogOpen(true);
                                    } else {
                                      setShowUSOverrides(checked);
                                    }
                                  }}
                                />
                                <div>
                                  <div className="text-[14px] font-medium leading-[20px] text-neutral-900">
                                    Advanced Banner Visibility Options
                                  </div>
                                  <div className="mt-[2px] text-[14px] leading-[20px] text-neutral-600">
                                    Choices will override default banner visibility for selected
                                    states
                                  </div>
                                </div>
                              </div>
                              {showUSOverrides && (
                                <ConsentManagementVariantCustomRow
                                  tableHeader="States"
                                  customVariants={customVariantsUSWithoutFlorida}
                                  allowedRegions={customVariantsUSAllowedRegions}
                                  onUpdate={handleCustomVariantsUpdate}
                                  showTooltip
                                />
                              )}
                              {showUSCallout && <NoCookiesCallout />}
                            </>
                          }
                        />

                        {showCanadaBannerSettings && (
                          <ConsentManagementVariantRow
                            title="Canada"
                            helpText="Canada's PIPEDA law requires that websites give visitors the choice to withdraw consent for data processing."
                            fields={["canadaConsentManagementVariant"]}
                            allowInformationalBanner={false}
                            allowOptOutBanner={false}
                            allowOptInBanner={true}
                            iconPath="/assets/images/flag-canada.svg"
                          />
                        )}
                        {showCanadaBannerSettings && showCanadaCallout && (
                          <div className="pb-[20px]">
                            <NoCookiesCallout />
                          </div>
                        )}

                        {showGDPRBannerSettings && (
                          <ConsentManagementVariantRow
                            title="EEA, UK, and Switzerland"
                            helpText="The European e-privacy law requires businesses to obtain consent before collecting data via web cookies (GDPR Banner)."
                            fields={["eeaukConsentManagementVariant"]}
                            allowInformationalBanner={false}
                            allowOptOutBanner={false}
                            allowOptInBanner={true}
                            iconPath="/assets/images/flag-eea.svg"
                            onStateLabel="GDPR banner"
                            titleTrailingAdornment={
                              <SeeList label="See countries">
                                {EEA_UK_CH_COUNTRY_NAMES.split(", ").map((name) => (
                                  <span key={name} className="block">
                                    {name}
                                  </span>
                                ))}
                              </SeeList>
                            }
                          />
                        )}
                        {showGDPRBannerSettings && showEEAUKCallout && (
                          <div className="pb-[20px]">
                            <NoCookiesCallout />
                          </div>
                        )}

                        {showGlobalConsentSettings && (
                          <ConsentManagementVariantRow
                            title="Global consent"
                            helpText="Display a consent banner in select countries based on your business needs."
                            fields={["globalConsentManagementVariantDefault"]}
                            allowInformationalBanner={false}
                            allowOptOutBanner={true}
                            allowOptInBanner={true}
                            iconPath="/assets/images/flag-globe.svg"
                            tableHeader="Default"
                            forceTableLayout
                            reverseOptionOrder
                            showTooltip
                            joinedBottom={showGlobalConsent && showGlobalOverridesTable}
                            tableTrailingPlaceholder={showGlobalConsent && showGlobalOverridesTable}
                            rowToggled={showGlobalConsent}
                            onRowToggle={(checked) => {
                              if (
                                !checked &&
                                ((globalDefaultVariant &&
                                  globalDefaultVariant !== ConsentManagementVariant.NONE) ||
                                  globalCustomVariants?.length > 0)
                              ) {
                                setConfirmClearGlobalConsentDialogOpen(true);
                              } else {
                                setShowGlobalConsent(checked);
                              }
                            }}
                            fieldToLabel={{
                              globalConsentManagementVariantDefault: "All countries",
                            }}
                            fieldDescriptionToRowLabelProvider={() => (
                              <span className="flex flex-col gap-[2px]">
                                <span>All countries</span>
                                <span className="text-[12px] leading-[16px] text-neutral-600">
                                  Excludes the US, Canada, and EEA/UK/CH.
                                </span>
                              </span>
                            )}
                            tableAttachment={
                              showGlobalOverridesTable && (
                                <ConsentManagementVariantCustomRow
                                  tableHeader="Overrides"
                                  customVariants={globalCustomVariants}
                                  allowedRegions={OVERRIDEABLE_COUNTRIES}
                                  onUpdate={handleGlobalCustomVariantsUpdate}
                                  allowInformationalBanner={false}
                                  regionDisplayPrefix=""
                                  selectPlaceholder="Choose countries"
                                  addButtonLabel="Add country"
                                  joinedTop
                                  searchable
                                  searchPlaceholder="Search all countries"
                                  showTooltip
                                />
                              )
                            }
                            contentAfterTable={
                              !showGlobalOverridesTable && (
                                <AquiloButton
                                  color="tertiary"
                                  size="s"
                                  onClick={() => setIsAddingGlobalOverride(true)}
                                >
                                  <PlusIcon className="mr-2 h-[20px] w-[20px] text-gray-500" />
                                  Add country
                                </AquiloButton>
                              )
                            }
                          />
                        )}
                      </ConsentSection>

                      {showAppearanceSection && (
                        <ConsentSection
                          id="consent-appearance"
                          title="Appearance"
                          description="How the banner looks on your site."
                        >
                          <ConsentBannerBackgroundColorRow />
                          <ConsentBannerIconColorRow
                            usBannerEnabled={hasUSInteractiveBanner}
                            canadaBannerEnabled={canadaBannerEnabled}
                            eeaukBannerEnabled={eeaukBannerEnabled}
                            formApi={form as any}
                          />
                          <ConsentBannerScreenPositionRow />
                        </ConsentSection>
                      )}

                      {showTextSection && (
                        <ConsentSection
                          id="consent-text"
                          title="Banner text"
                          description="What each banner type says. We recommend the default text."
                        >
                          {hasUSInteractiveBanner && <ConsentBannerUSTitleRow formApi={form} />}

                          {hasOptInBanner && (
                            <>
                              <ConsentGroupHeader
                                title="Opt-in banner"
                                usedIn={usedInText(
                                  ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT,
                                )}
                              />
                              <ConsentBannerCopyOverrideRow
                                key={`opt-in-text-${copiedSettingsVersion}`}
                                formApi={form as any}
                                bannerName="opt-in"
                                description="The text on the English opt-in banner. We recommend the default text, but you may customize it to take full ownership of your on-page disclosure."
                                field="bannerTextOptInEn"
                                resetText={OPT_IN_BANNER_DEFAULT_TEXT}
                                startToggled={Boolean(values.bannerTextOptInEn)}
                              />
                              <ConsentBannerOptInButtonTextRow />
                            </>
                          )}

                          {hasOptOutBanner && (
                            <>
                              <ConsentGroupHeader
                                title="Opt-out banner"
                                usedIn={usedInText(
                                  ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT,
                                )}
                              />
                              <ConsentBannerCopyOverrideRow
                                key={`opt-out-text-${copiedSettingsVersion}`}
                                formApi={form as any}
                                bannerName="opt-out"
                                description="The text on the English opt-out banner. We recommend the default text, but you may customize it to take full ownership of your on-page disclosure."
                                field="bannerTextOptOutEn"
                                resetText={OPT_OUT_BANNER_DEFAULT_TEXT}
                                startToggled={Boolean(values.bannerTextOptOutEn)}
                              />
                              {acceptButtonInOptOutGroup && (
                                <ConsentBannerUSButtonRow
                                  formApi={form}
                                  helpText={acceptButtonHelpText}
                                />
                              )}
                            </>
                          )}

                          {hasUSInformationalBanner && (
                            <>
                              <ConsentGroupHeader
                                title="Informational banner"
                                usedIn={usedInText(ConsentManagementVariant.INFORMATIONAL_ONLY)}
                              />
                              <ConsentBannerUSTextRow
                                formApi={form}
                                informationalBannerDefaultText={INFORMATIONAL_BANNER_DEFAULT_TEXT}
                              />
                              <ConsentBannerUSDismissalRow />
                              {acceptButtonInInformationalGroup && (
                                <ConsentBannerUSButtonRow
                                  formApi={form}
                                  helpText={acceptButtonHelpText}
                                />
                              )}
                            </>
                          )}

                          {hasDismissButtonTextBanner && (
                            <>
                              <ConsentGroupHeader
                                title="Preferences screen"
                                usedIn="Opens from the opt-in and opt-out banners"
                              />
                              <ConsentBannerDismissButtonTextRow />
                              {showPreferencesDisclaimerSettings && (
                                <ConsentBannerPreferencesDisclaimerRow />
                              )}
                            </>
                          )}
                        </ConsentSection>
                      )}

                      {showBannerVisualSettings && (
                        <ConsentSection id="consent-advanced" title="Advanced options">
                          <ConsentBannerConsentRequiredRow />
                          <ConsentBannerAllowCSSOverridesRow />
                        </ConsentSection>
                      )}

                      {showPreviewSection && (
                        <ConsentSection
                          id="consent-preview"
                          title="Preview"
                          description="See the banner on a page of your site before you publish."
                        >
                          <ConsentBannerPreviewURLRow formApi={form as any} />
                          <ConsentSettingRow
                            title="Preview a region"
                            helpText="Opens the banner the way visitors in that region see it."
                          >
                            {previewContent}
                          </ConsentSettingRow>
                        </ConsentSection>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <RegisterField name="customConsentManagementVariants" />
              <RegisterField name="globalConsentManagementVariants" />
            </>
          );
        }}
      />
    </>
  );
});

interface UseConsentBannerSettings {
  settings: CMPSettingsDto;
  privacyCenter: PrivacyCenterDto;
  privacyCenterRequest: NetworkRequestState;
  cmpCustomizationRequest: NetworkRequestState;

  getLatestData: () => Promise<unknown>;
}

export const isWebsiteConsentSetupComplete = (
  settings: CMPSettingsDto,
  organization: OrganizationDto,
) => {
  const hasEeaUkConsentManagementVariant =
    settings?.eeaukConsentManagementVariant != null ||
    settings?.gdprConsentManagementEnabled === true;

  return (
    (settings?.canadaConsentManagementVariant != null || !organization.featurePipeda) &&
    settings?.usConsentManagementVariantNonUSP != null &&
    (settings?.usConsentManagementVariantCA != null ||
      settings?.usConsentManagementVariantMinors != null) &&
    settings?.usConsentManagementVariantUSP != null &&
    (hasEeaUkConsentManagementVariant || !organization.featureGdpr)
  );
};

/** The fields the Consent Banner form edits, picked out of a CMP customization response. */
const toConsentBannerSettings = (
  cmpCustomization: CMPSettingsDto | null | undefined,
): CMPSettingsDto => ({
  informationalConsentManagementVariant:
    cmpCustomization?.informationalConsentManagementVariant === true,
  informationalBannerAcceptButtonText: cmpCustomization?.informationalBannerAcceptButtonText,
  informationalBannerDismissalStyle: cmpCustomization?.informationalBannerDismissalStyle,
  informationalBannerText: cmpCustomization?.informationalBannerText,
  informationalBannerTitle: cmpCustomization?.informationalBannerTitle,
  bannerTextOptInEn: cmpCustomization?.bannerTextOptInEn,
  bannerTextOptOutEn: cmpCustomization?.bannerTextOptOutEn,
  bannerCopyVariant: cmpCustomization?.bannerCopyVariant,
  bannerDismissalButtonCopyVariant: cmpCustomization?.bannerDismissalButtonCopyVariant,
  bannerPreferencesDisclaimerTextEn: cmpCustomization?.bannerPreferencesDisclaimerTextEn,

  usConsentManagementVariant: cmpCustomization?.usConsentManagementVariant,
  usConsentManagementVariantCA: cmpCustomization?.usConsentManagementVariantCA,
  usConsentManagementVariantUSP: cmpCustomization?.usConsentManagementVariantUSP,
  usConsentManagementVariantNonUSP: cmpCustomization?.usConsentManagementVariantNonUSP,
  usConsentManagementVariantMinors: cmpCustomization?.usConsentManagementVariantMinors,
  eeaukConsentManagementVariant: cmpCustomization?.eeaukConsentManagementVariant,
  canadaConsentManagementVariant: cmpCustomization?.canadaConsentManagementVariant,
  customConsentManagementVariants: cmpCustomization?.customConsentManagementVariants,
  globalConsentManagementVariantDefault: cmpCustomization?.globalConsentManagementVariantDefault,
  globalConsentManagementVariants: cmpCustomization?.globalConsentManagementVariants,

  usBannerVisibility: cmpCustomization?.usBannerVisibility,

  bannerBackgroundColor: cmpCustomization?.bannerBackgroundColor,
  bannerIconColorPrimary: cmpCustomization?.bannerIconColorPrimary,
  bannerIconColorSecondary: cmpCustomization?.bannerIconColorSecondary,

  bannerScreenPosition: cmpCustomization?.bannerScreenPosition,

  gdprConsentManagementEnabled: cmpCustomization?.gdprConsentManagementEnabled,
  previewURL: cmpCustomization?.previewURL,

  consentRequiredMode: cmpCustomization?.consentRequiredMode,
  consentChoiceRequiredBannerTypes: cmpCustomization?.consentChoiceRequiredBannerTypes,
  allowCSSOverrides: cmpCustomization?.allowCSSOverrides,

  orgInfo: cmpCustomization?.orgInfo,
});

export const useConsentBannerSettings = (): UseConsentBannerSettings => {
  const organizationId = usePrimaryOrganizationId();

  const [privacyCenter, privacyCenterRequest, _fetch] = usePrivacyCenter(organizationId);
  const [cmpCustomization, cmpCustomizationRequest, getLatestData] = useCMPCustomization(
    organizationId,
    privacyCenter?.id,
  );

  return {
    settings: toConsentBannerSettings(cmpCustomization),
    privacyCenter,
    privacyCenterRequest,
    cmpCustomizationRequest,
    getLatestData,
  };
};

interface UseConsentBannerStagingSettings {
  // Null until the org first publishes to staging.
  stagingSettings: CMPSettingsDto | null;
  stagingSettingsRequest: NetworkRequestState;
  getLatestStagingData: () => Promise<unknown>;
}

/** Settings saved for the staging script, which the Consent Banner tab edits in its staging environment. */
export const useConsentBannerStagingSettings = (
  privacyCenterId: string | undefined,
): UseConsentBannerStagingSettings => {
  const organizationId = usePrimaryOrganizationId();
  const [stagingCustomization, stagingSettingsRequest, getLatestStagingData] =
    useCMPStagingCustomization(organizationId, privacyCenterId);
  const savedSettings = stagingCustomization?.settings;

  return {
    stagingSettings: savedSettings ? toConsentBannerSettings(savedSettings) : null,
    stagingSettingsRequest,
    getLatestStagingData,
  };
};
